import React from "react";
import { Bolt, Cancel, CheckCircle, ChevronRight, Error as ErrorIcon, Sync } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { BASE_DATA_LAYERS } from "./layers";
import type { BaseDataRowModel } from "./model";
import { BaseDataRowStatus } from "./rowState";

/** Statuses whose caption is shown under the row's name. */
const CAPTIONED_STATUSES: BaseDataRowStatus[] = ["loading", "running", "failed"];

interface BaseDataRowProps {
  row: BaseDataRowModel;
  selected: boolean;
  disabled: boolean;
  onClick(): void;
}

const StatusIcon: React.FC<{ status: BaseDataRowStatus; caption: string }> = ({ status, caption }) => {
  switch (status) {
    case "done":
      return <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 18 }} />;
    case "partial":
      return (
        <Tooltip title={caption}>
          <ErrorIcon sx={{ color: (theme) => theme.palette.warning.main, fontSize: 18 }} />
        </Tooltip>
      );
    case "required":
      return (
        <Tooltip title={caption}>
          <ErrorIcon sx={{ color: (theme) => theme.palette.biarri.secondary.inactiveGrey, fontSize: 18 }} />
        </Tooltip>
      );
    case "ready":
      return (
        <Tooltip title={caption}>
          <Bolt sx={{ color: "common.white", bgcolor: "primary.light", borderRadius: "50%", fontSize: 18 }} />
        </Tooltip>
      );
    case "unavailable":
      return (
        <Tooltip title={`Unable to generate. ${caption}`}>
          <Bolt
            aria-label="Unable to generate"
            sx={{ color: "common.white", bgcolor: (theme) => theme.palette.biarri.secondary.inactiveGrey, borderRadius: "50%", fontSize: 18 }}
          />
        </Tooltip>
      );
    case "stale":
      return (
        <Tooltip title="Out of date. Generate again to update">
          <Sync sx={{ color: "common.white", bgcolor: "warning.main", borderRadius: "50%", fontSize: 18 }} />
        </Tooltip>
      );
    case "loading":
    case "running":
      return <CircularProgress size={16} color="warning" aria-label={caption} />;
    case "failed":
      return <Cancel aria-label={caption} sx={{ color: "error.main", fontSize: 18 }} />;
    default:
      return null;
  }
};

/** A base data layer row, showing the layer's status and opening its flyout when clicked. */
const BaseDataRow: React.FC<BaseDataRowProps> = ({ row, selected, disabled, onClick }) => {
  const { name, icon: Icon } = BASE_DATA_LAYERS[row.key];
  const { status, caption, count } = row;

  return (
    <NavigationButton
      icon={<Icon />}
      iconSize="small"
      title={
        <>
          {name}
          {CAPTIONED_STATUSES.includes(status) && (
            <Typography
              component="span"
              display="block"
              fontSize={11}
              lineHeight={1.4}
              fontWeight={400}
              color={(theme) => (status === "failed" ? theme.palette.error.main : theme.palette.biarri.secondary.grey)}
            >
              {caption}
            </Typography>
          )}
        </>
      }
      checked={status === "done"}
      disabled={disabled}
      onClick={onClick}
      variant="outlined"
      sx={{
        my: 0,
        borderRadius: 0,
        "&:not(:first-of-type)": { mt: "-1px" },
        "&:first-of-type": { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
        "&:last-of-type": { borderBottomLeftRadius: 4, borderBottomRightRadius: 4 },
        ...(selected && { backgroundColor: "rgba(33, 150, 243, 0.05)", borderColor: "primary.main", zIndex: 1 }),
      }}
      rightElement={
        <Box display="flex" alignItems="center" gap={1}>
          {count !== null && (
            <Chip
              size="small"
              title={caption}
              label={
                <Typography variant="body3" fontWeight={400}>
                  {count.toLocaleString()}
                </Typography>
              }
            />
          )}
          <StatusIcon status={status} caption={caption} />
          <ChevronRight sx={{ fontSize: 20, color: selected ? "primary.main" : "action.disabled" }} />
        </Box>
      }
    />
  );
};

export default BaseDataRow;
